import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeft, MapPin, CheckCircle, Search, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { useApi } from './DashboardLayout';

function AddressSearch({ apiFetch, onSelect, addressValidated, onClear }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const search = (value) => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length < 3) { setResults([]); setShowDropdown(false); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await apiFetch(`/api/geocode/search?q=${encodeURIComponent(value)}`);
        const d = await r?.json();
        if (d?.results) { setResults(d.results); setShowDropdown(d.results.length > 0); }
      } catch (e) { /* silent */ }
      setSearching(false);
    }, 400);
  };

  const selectResult = (item) => {
    setShowDropdown(false);
    setQuery(item.display_name);
    onSelect({
      veranstaltungsort_strasse: item.street,
      veranstaltungsort_plz: item.postal_code,
      veranstaltungsort_ort: item.city,
      location_lat: item.lat,
      location_long: item.lng,
    });
  };

  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    onClear();
  };

  return (
    <div style={{ position: 'relative', marginBottom: 12 }} ref={wrapperRef}>
      <label className="of-label" style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <MapPin size={12} /> Adresse suchen
        {addressValidated && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, color: '#16a34a', background: '#dcfce7', padding: '1px 6px', borderRadius: 20, marginLeft: 4 }} data-testid="address-validated-badge">
            <CheckCircle size={10} /> Geprüft
          </span>
        )}
      </label>
      <div style={{ position: 'relative', display: 'flex', gap: 6 }}>
        <div style={{ position: 'relative', flex: 1 }}>
          {searching
            ? <Loader2 size={14} className="spin" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)' }} />
            : <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          }
          <input className="of-input" style={{ paddingLeft: 32 }} value={query} onChange={e => search(e.target.value)}
            placeholder={addressValidated ? 'Erneut suchen um Adresse zu ändern...' : 'Straße + Nr. eingeben...'}
            data-testid="address-autocomplete" />
        </div>
        {addressValidated && (
          <button type="button" onClick={handleClear} data-testid="address-clear-btn"
            style={{ padding: '0 10px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Zurücksetzen
          </button>
        )}
      </div>
      {showDropdown && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, background: 'var(--card-bg, #fff)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 200, overflowY: 'auto', marginTop: 2 }} data-testid="address-suggestions">
          {results.map((item, i) => (
            <div key={i} onClick={() => selectResult(item)} data-testid={`address-suggestion-${i}`}
              style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, borderBottom: i < results.length - 1 ? '1px solid var(--border)' : 'none', display: 'flex', alignItems: 'flex-start', gap: 6 }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <MapPin size={12} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 500 }}>{item.street || item.display_name.split(',')[0]}</div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{item.postal_code} {item.city}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProviderSearch({ providers, value, onChange }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selected = providers.find(p => p.id === value);
  const normalize = (s) => (s || '').toLowerCase().replace(/[-./,()]/g, ' ').replace(/\s+/g, ' ').trim();
  const filtered = query.length > 0
    ? (() => {
        const words = normalize(query).split(' ').filter(Boolean);
        return providers.filter(p => {
          const haystack = [p.verein, p.ik_number, p.club_id, p.city_location].map(normalize).join(' ');
          return words.every(w => haystack.includes(w));
        });
      })()
    : providers;

  const selectProvider = (p) => {
    onChange(p.id);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="of-card" style={{ marginBottom: 12, position: 'relative' }} ref={wrapperRef}>
      <label className="of-label"><Search size={11} style={{ verticalAlign: 'middle' }} /> Anbieter</label>
      {selected ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ flex: 1, padding: '7px 10px', fontSize: 13, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--page-bg)', fontWeight: 500 }} data-testid="offer-provider-selected">
            {selected.verein} <span style={{ fontFamily: 'monospace', color: 'var(--text-muted)', fontSize: 12 }}>IK {selected.ik_number} &middot; Club-ID {selected.club_id}</span>
          </div>
          <button type="button" onClick={() => { onChange(0); setQuery(''); }} style={{ padding: '6px 12px', fontSize: 11, fontWeight: 600, border: '1px solid var(--border)', borderRadius: 6, background: 'var(--card-bg)', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }} data-testid="offer-provider-clear">
            Ändern
          </button>
        </div>
      ) : (
        <>
          <div style={{ position: 'relative' }}>
            <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
            <input className="of-input" style={{ paddingLeft: 32 }}
              value={query}
              onChange={e => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Vereinsname, IK-Nummer oder Club-ID eingeben..."
              data-testid="offer-provider-search" />
          </div>
          {open && (
            <div style={{
              position: 'absolute', left: 0, right: 0, zIndex: 50, marginTop: 4,
              background: 'var(--card-bg, #fff)', border: '1px solid var(--border)', borderRadius: 8,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', maxHeight: 240, overflowY: 'auto'
            }} data-testid="offer-provider-dropdown">
              {filtered.length === 0 ? (
                <div style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>Kein Anbieter gefunden</div>
              ) : filtered.map(p => (
                <div key={p.id} onClick={() => selectProvider(p)} data-testid={`provider-option-${p.id}`}
                  style={{ padding: '8px 14px', cursor: 'pointer', fontSize: 13, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span style={{ fontWeight: 500 }}>{p.verein}</span>
                  <span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-muted)' }}>IK {p.ik_number} &middot; {p.club_id}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label className="of-label">{label}</label>
    {children}
  </div>
);

const TF = ({ label, field, value, onChange, type = 'text', placeholder, readOnly, style }) => (
  <Field label={label}>
    <input className="of-input" type={type} value={value} onChange={e => onChange(field, e.target.value)}
      placeholder={placeholder} readOnly={readOnly} style={{ ...style, ...(readOnly ? { background: 'var(--page-bg)', cursor: 'not-allowed' } : {}) }} />
  </Field>
);

const SF = ({ label, field, value, onChange, options }) => (
  <Field label={label}>
    <select className="of-input" value={value} onChange={e => onChange(field, e.target.value)}>
      <option value="">—</option>
      {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  </Field>
);

export default function OfferForm({ userType }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const fromProvider = searchParams.get('from_provider');
  const presetProvider = searchParams.get('provider_id');
  const backUrl = searchParams.get('back');
  const isEdit = !!id;
  const navigate = useNavigate();
  const { apiFetch } = useApi();
  const [form, setForm] = useState({
    provider_id: parseInt(presetProvider || localStorage.getItem('bgpr_user_id') || '0'),
    angebotsnummer: '', hauptindikation: '', rehabilitationssportart: '',
    abrechenbare_positionsnummer: '', angebot: '', zusatzliche_bezeichnung: '',
    veranstaltungsort_name: '', veranstaltungsort_strasse: '', veranstaltungsort_ort: '',
    veranstaltungsort_plz: '', location_long: '', location_lat: '', bezirk: '',
    wochentag: '', uhrzeit_von: '', uhrzeit_bis: '', name_ubungsleiter: '',
    lizenz: '', lizenz_nummer: '', gultig_bis: '', notfallabsicherung: '',
    anerkannt_seit: '', anerkannt_bis: ''
  });
  const [dropdowns, setDropdowns] = useState({});
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressValidated, setAddressValidated] = useState(false);
  const [indikationMap, setIndikationMap] = useState({});
  const [siblingOffers, setSiblingOffers] = useState([]);
  const [deregInfo, setDeregInfo] = useState(null); // { deregistration_date, deregistered_at, deregistered_by }
  const [showDeregForm, setShowDeregForm] = useState(false);
  const [deregDate, setDeregDate] = useState('');

  useEffect(() => {
    apiFetch('/api/dropdowns').then(r => r?.json()).then(d => {
      if (d) { const map = {}; d.forEach(dd => { map[dd.name] = Array.isArray(dd.dropdown_values) ? dd.dropdown_values : []; }); setDropdowns(map); }
    });
    apiFetch('/api/indikation-mapping').then(r => r?.json()).then(d => {
      if (d?.mappings) setIndikationMap(d.mappings);
    });
    if (userType <= 2) apiFetch('/api/providers').then(r => r?.json()).then(d => d && setProviders(d));
  }, []);

  useEffect(() => {
    if (isEdit) {
      apiFetch(`/api/offers/${id}`).then(r => r?.json()).then(d => {
        if (d && !d.detail) {
          setForm(prev => { const u = { ...prev }; Object.keys(u).forEach(k => { if (d[k] !== undefined) u[k] = d[k] || ''; }); return u; });
          // Deregistration info
          if (d.deregistration_date) {
            setDeregInfo({ deregistration_date: d.deregistration_date, deregistered_at: d.deregistered_at, deregistered_by: d.deregistered_by });
          } else {
            setDeregInfo(null);
          }
          // Load sibling offers for this provider if coming from provider
          const pid = fromProvider || d.provider_id;
          if (pid && siblingOffers.length === 0) {
            apiFetch(`/api/offers?provider_id=${pid}`).then(r2 => r2?.json()).then(offers => {
              if (Array.isArray(offers)) setSiblingOffers(offers);
            });
          }
        }
      });
      setAddressValidated(false);
    }
  }, [id]);

  const currentOfferIdx = siblingOffers.findIndex(o => o.id === parseInt(id));
  const prevOffer = currentOfferIdx > 0 ? siblingOffers[currentOfferIdx - 1] : null;
  const nextOffer = currentOfferIdx >= 0 && currentOfferIdx < siblingOffers.length - 1 ? siblingOffers[currentOfferIdx + 1] : null;

  const navToOffer = (offerId) => {
    const params = new URLSearchParams(searchParams);
    navigate(`/app/offers/${offerId}/edit?${params.toString()}`);
  };

  const handleDeregister = async () => {
    if (!deregDate) return;
    const r = await apiFetch(`/api/offers/${id}/deregister`, { method: 'POST', body: JSON.stringify({ deregistration_date: deregDate }) });
    if (r?.ok) {
      const d = await r.json();
      setDeregInfo({ deregistration_date: deregDate, deregistered_at: new Date().toISOString(), deregistered_by: '' });
      setShowDeregForm(false);
      setDeregDate('');
    } else {
      const d = await r?.json(); setError(d?.detail || 'Fehler bei Abmeldung');
    }
  };

  const handleCancelDeregister = async () => {
    const r = await apiFetch(`/api/offers/${id}/cancel-deregister`, { method: 'POST' });
    if (r?.ok) {
      setDeregInfo(null);
    } else {
      const d = await r?.json(); setError(d?.detail || 'Fehler');
    }
  };

  const goBack = () => {
    if (backUrl) { navigate(backUrl); return; }
    if (fromProvider) navigate(`/app/providers?view=${fromProvider}`);
    else navigate(-1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true); setError('');
    try {
      const r = await apiFetch(isEdit ? `/api/offers/${id}` : '/api/offers', { method: isEdit ? 'PUT' : 'POST', body: JSON.stringify(form) });
      const d = await r?.json();
      if (r?.ok) goBack(); else setError(d?.detail || 'Fehler');
    } catch (err) { setError(err.message); }
    setLoading(false);
  };

  const set = useCallback((k, v) => {
    setForm(p => {
      const updated = { ...p, [k]: v };
      if (k === 'hauptindikation' && indikationMap[v]) {
        updated.abrechenbare_positionsnummer = indikationMap[v];
      }
      return updated;
    });
    if (['veranstaltungsort_strasse', 'veranstaltungsort_plz', 'veranstaltungsort_ort'].includes(k)) {
      setAddressValidated(false);
    }
  }, [indikationMap]);

  const handleAddressSelect = useCallback((data) => {
    setForm(p => ({ ...p, ...data }));
    setAddressValidated(true);
  }, []);

  return (
    <div data-testid="offer-form-page">

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-secondary" style={{ padding: '6px 14px', fontSize: 13 }} onClick={goBack} data-testid="offer-back-btn">
            <ArrowLeft size={14} /> {fromProvider ? 'Zurück zum Anbieter' : 'Zurück'}
          </button>
          <h2 style={{ margin: 0, fontSize: 20 }}>{isEdit ? 'Angebot bearbeiten' : 'Neues Angebot'}</h2>
        </div>
        {siblingOffers.length > 1 && currentOfferIdx >= 0 && (
          <div className="detail-nav-pager" data-testid="offer-pager">
            <button className="btn btn-secondary btn-sm" onClick={() => navToOffer(prevOffer.id)} disabled={!prevOffer} data-testid="offer-prev-btn">
              <ChevronLeft size={14} /> Vorheriges
            </button>
            <span className="detail-nav-count">{currentOfferIdx + 1} / {siblingOffers.length}</span>
            <button className="btn btn-secondary btn-sm" onClick={() => navToOffer(nextOffer.id)} disabled={!nextOffer} data-testid="offer-next-btn">
              Nächstes <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {error && <div style={{ background: '#FEE2E2', color: '#991B1B', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 12, fontWeight: 500 }}>{error}</div>}

      {/* Abmeldung Banner */}
      {isEdit && deregInfo && (
        <div className="of-card" style={{ marginBottom: 12, borderColor: '#F59E0B', background: '#FFFBEB' }} data-testid="deregistration-banner">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Abmeldung zum {deregInfo.deregistration_date?.split('-').reverse().join('.')}
              </div>
              <div style={{ fontSize: 11, color: '#A16207', marginTop: 2 }}>
                Nach dem Abmeldedatum dürfen keine weiteren Stunden bei den Krankenkassen abgerechnet werden. Die Zertifizierung entfällt und das Angebot wird nicht mehr gelistet.
              </div>
            </div>
            <button className="btn btn-sm" style={{ background: '#92400E', color: '#fff', padding: '4px 12px', fontSize: 12 }}
              onClick={handleCancelDeregister} data-testid="cancel-deregister-btn">
              Abmeldung zurücknehmen
            </button>
          </div>
        </div>
      )}

      {/* Abmeldung Button / Form */}
      {isEdit && !deregInfo && (
        <>
          {showDeregForm ? (
            <div className="of-card" style={{ marginBottom: 12, borderColor: '#EF4444' }} data-testid="deregister-form">
              <div className="of-section" style={{ color: '#DC2626', borderColor: '#DC2626' }}>Angebot abmelden</div>
              <div style={{ fontSize: 12, color: '#92400E', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6, padding: '8px 12px', marginBottom: 12, lineHeight: 1.5 }}>
                Nach dem Abmeldedatum dürfen keine weiteren Stunden zu diesem Angebot bei den Krankenkassen abgerechnet werden. Die Krankenkassen werden über die Abmeldung informiert. Die Zertifizierung entfällt und das Angebot wird nicht mehr gelistet.
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                <div style={{ flex: 1, maxWidth: 250 }}>
                  <label className="of-label">Abmeldung zum</label>
                  <input className="of-input" type="date" value={deregDate} onChange={e => setDeregDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]} data-testid="deregister-date-input" />
                </div>
                <button className="btn btn-sm" style={{ background: '#DC2626', color: '#fff', padding: '7px 16px', fontSize: 12 }}
                  onClick={handleDeregister} disabled={!deregDate} data-testid="confirm-deregister-btn">
                  Abmelden
                </button>
                <button className="btn btn-secondary btn-sm" style={{ padding: '7px 12px', fontSize: 12 }}
                  onClick={() => { setShowDeregForm(false); setDeregDate(''); }}>
                  Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-sm btn-secondary" style={{ color: '#DC2626', borderColor: '#DC2626', fontSize: 12 }}
                onClick={() => setShowDeregForm(true)} data-testid="show-deregister-btn">
                Angebot abmelden
              </button>
            </div>
          )}
        </>
      )}

      <form onSubmit={handleSubmit}>
        {/* Anbieter search - full width */}
        {userType <= 2 && (
          <ProviderSearch providers={providers} value={form.provider_id} onChange={id => set('provider_id', id)} />
        )}

        {/* Two column layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>

          {/* LEFT: Angebot */}
          <div className="of-card">
            <div className="of-section">Angebot</div>
            <SF label="Hauptindikation" field="hauptindikation" value={form.hauptindikation} onChange={set} options={dropdowns['Hauptindikation']} />
            <SF label="Rehabilitationssportart" field="rehabilitationssportart" value={form.rehabilitationssportart} onChange={set} options={dropdowns['Rehabilitationssportart']} />
            <div className="of-2col">
              <SF label="Angebot" field="angebot" value={form.angebot} onChange={set} options={dropdowns['Angebot']} />
              <SF label="Zusätzl. Bezeichnung" field="zusatzliche_bezeichnung" value={form.zusatzliche_bezeichnung} onChange={set} options={dropdowns['zusätzliche Bezeichnung des Angebots']} />
            </div>
            <Field label="Positionsnr.">
              <input className="of-input" value={form.abrechenbare_positionsnummer} readOnly tabIndex={-1}
                style={{ background: 'var(--page-bg)', cursor: 'not-allowed', fontFamily: 'monospace', fontWeight: 600 }}
                data-testid="positionsnr-readonly" />
              {form.hauptindikation && !form.abrechenbare_positionsnummer && (
                <div style={{ fontSize: 11, color: '#b45309', marginTop: 3 }}>Keine Positionsnr. hinterlegt</div>
              )}
            </Field>
          </div>

          {/* RIGHT: Veranstaltungsort */}
          <div className="of-card">
            <div className="of-section">Veranstaltungsort</div>
            <AddressSearch apiFetch={apiFetch} onSelect={handleAddressSelect} addressValidated={addressValidated} onClear={() => {
              setForm(p => ({ ...p, veranstaltungsort_strasse: '', veranstaltungsort_plz: '', veranstaltungsort_ort: '', location_lat: '', location_long: '' }));
              setAddressValidated(false);
            }} />
            <TF label="Name der Sportstätte" field="veranstaltungsort_name" value={form.veranstaltungsort_name} onChange={set} />
            <div className="of-2col">
              <TF label="Straße + Hausnr." field="veranstaltungsort_strasse" value={form.veranstaltungsort_strasse} onChange={set} readOnly />
              <SF label="Bezirk" field="bezirk" value={form.bezirk} onChange={set} options={dropdowns['Bezirk']} />
            </div>
            <div className="of-2col">
              <TF label="PLZ" field="veranstaltungsort_plz" value={form.veranstaltungsort_plz} onChange={set} readOnly />
              <TF label="Ort" field="veranstaltungsort_ort" value={form.veranstaltungsort_ort} onChange={set} readOnly />
            </div>
            {addressValidated && form.location_lat && form.location_long && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 4, marginTop: -4 }} data-testid="coordinates-display">
                <MapPin size={11} /> {parseFloat(form.location_lat).toFixed(5)}, {parseFloat(form.location_long).toFixed(5)}
              </div>
            )}
          </div>
        </div>

        {/* Bottom row: Zeit & Übungsleiter */}
        <div className="of-card" style={{ marginBottom: 16 }}>
          <div className="of-section">Zeit & Übungsleiter</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 16px' }}>
            <SF label="Wochentag" field="wochentag" value={form.wochentag} onChange={set} options={dropdowns['Wochentag']} />
            <TF label="Uhrzeit von" field="uhrzeit_von" value={form.uhrzeit_von} onChange={set} type="time" />
            <TF label="Uhrzeit bis" field="uhrzeit_bis" value={form.uhrzeit_bis} onChange={set} type="time" />
            <TF label="Übungsleiter" field="name_ubungsleiter" value={form.name_ubungsleiter} onChange={set} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '0 16px' }}>
            <SF label="Lizenz" field="lizenz" value={form.lizenz} onChange={set} options={dropdowns['Lizenz']} />
            <TF label="Lizenz-Nr." field="lizenz_nummer" value={form.lizenz_nummer} onChange={set} />
            <TF label="Gültig bis" field="gultig_bis" value={form.gultig_bis} onChange={set} type="date" />
            <SF label="Notfallabsicherung" field="notfallabsicherung" value={form.notfallabsicherung} onChange={set} options={dropdowns['Notfallabsicherung']} />
          </div>
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-primary" type="submit" disabled={loading} data-testid="offer-submit-btn" style={{ padding: '8px 24px' }}>
            {loading ? 'Speichern...' : isEdit ? 'Aktualisieren' : 'Erstellen'}
          </button>
          <button className="btn btn-secondary" type="button" onClick={goBack} style={{ padding: '8px 20px' }}>Abbrechen</button>
        </div>
      </form>
    </div>
  );
}
